import React, {useRef, useState} from 'react';
import {useResizeObserver} from 'usehooks-ts';

import BackButton from '@/components/BackButton/BackButton';
import {ArrowLeftIcon} from '@/components/Icons';
import {ScrollArea} from '@/components/ScrollArea/ScrollArea';
import ROUTE_PATHS from '@/consts/route/routePaths';
import HomePageTopNavigation from '@/containers/HomePageTopNavigation/HomePageTopNavigation';
import useLayout from '@/hooks/layout/useLayout';
import {twx} from '@/utils/tailwindUtils';
import HomePageLayout from '@/views/Home/HomePageLayout';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

interface LayoutProps {
    children: React.ReactNode;
}

interface StartProps {
    children: React.ReactNode;
    onBack?: () => void;
    action?: React.ReactNode;
    footer?: React.ReactNode;
}

interface EndProps {
    children: React.ReactNode;
    footer?: React.ReactNode;
}

const AutoDeliveryDcxPageDesktopLayout = ({children}: LayoutProps) => {
    return (
        <HomePageLayout className='bg-primary-dark-5' activePath={ROUTE_PATHS.HOME_PAGE}>
            <WarningBannersTop
                source={WARNING_BANNER_SOURCE_TYPES.AUTO_DELIVERY}
                className='ml:pl-[var(--sidebar-width)]'
            />
            {/* @ts-expect-error */}
            <HomePageTopNavigation profileControlsClassName='bg-romance' />
            <div className='flex h-[calc(100vh-var(--hw-h))] min-h-full px-16 pt-24'>
                <div className='flex w-full flex-1 gap-4'>{children}</div>
            </div>
        </HomePageLayout>
    );
};
const Start = ({children, onBack, action, footer}: StartProps) => {
    const {isDesktop} = useLayout();

    return (
        <div className='flex w-full flex-col overflow-hidden'>
            {/* @ts-expect-error */}
            <ScrollArea
                className='flex-1 pr-4'
                viewportClassName='snap-y snap-proximity [&>div]:flex [&>div]:min-h-full [&>div]:flex-col pb-4'
            >
                <div className='mb-4 flex snap-start items-center justify-between'>
                    {/* @ts-expect-error */}
                    <BackButton onClick={onBack} icon={isDesktop && <ArrowLeftIcon />} />
                    {action}
                </div>
                {children}
            </ScrollArea>
            {footer && <div className='shrink-0 border-t border-mischka pb-8 pr-4 pt-4'>{footer}</div>}
        </div>
    );
};

const End = ({children, footer}: EndProps) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const [isScrollVisible, setIsScrollVisible] = useState(false);

    const checkScroll = () => {
        if (!viewportRef.current) return;

        const {scrollHeight, clientHeight} = viewportRef.current;

        setIsScrollVisible(scrollHeight > clientHeight);
    };

    useResizeObserver({ref: viewportRef, onResize: checkScroll});

    return (
        <div className='flex w-[45%] shrink-0 flex-col overflow-hidden rounded-t-[30px] bg-romance'>
            {/* @ts-expect-error */}
            <ScrollArea className='flex-1' viewportClassName='[&>div]:h-full' viewportRef={viewportRef}>
                <div className='flex h-full flex-col'>
                    <div className='flex w-full flex-1 flex-col px-8 pb-0 pt-8'>
                        <div className='divide-y divide-primary-dark-15 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0 [&>*]:py-4'>
                            {children}
                        </div>
                    </div>
                    {/* @ts-expect-error */}
                    <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.AUTO_DELIVERY} />
                </div>
            </ScrollArea>
            {footer && (
                <div className={twx('shrink-0 px-8 pb-8 pt-4', isScrollVisible && 'bg-primary-dark-15')}>{footer}</div>
            )}
        </div>
    );
};

AutoDeliveryDcxPageDesktopLayout.Start = Start;
AutoDeliveryDcxPageDesktopLayout.End = End;

export default AutoDeliveryDcxPageDesktopLayout;
